import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { OnboardingModulesApi } from '@frontend/api-onboarding-modules';
import { authnClient } from '@frontend/auth';
import { setLoginData } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMutation } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { UsersTypes } from '@frontend/user-helpers';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  Heading,
  NakedButton,
  PhoneField,
  SpinningLoader,
  Text,
  TextField,
  useAlert,
  useTooltip,
} from '@frontend/design-system';
import { useValidatePassword } from '../../hooks/use-validate-password';
import { maskEmail } from '../../utils';
import { useSignupUserForm } from '../user-forms';
import { UserCreationTrackingIds } from './tracking-ids';

export interface SignUpFormUserInfo {
  email?: string;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  userId?: string;
}

interface SignupFormProps {
  userInfo?: SignUpFormUserInfo;
  nonceToken: string;
  locationId?: string;
}

type PutUserPayloadV2 = UsersTypes.PutUserPayload & {
  LocationId: string;
  Profile: { FirstName?: string; LastName?: string; MobileNumber?: string; UserId?: string };
};

export const SignupForm: FC<SignupFormProps> = ({ userInfo, nonceToken, locationId = '' }) => {
  const { t } = useTranslation('userCreate', { keyPrefix: 'signup-form' });
  const alerts = useAlert();
  const navigate = useNavigate();
  const { triggerProps, tooltipProps, Tooltip } = useTooltip();

  const [showPassword, setShowPassword] = useState(false);
  const [passwordFieldInFocus, setPasswordFieldInFocus] = useState(false);

  const { mutate, isLoading } = useMutation({
    mutationFn: (payload: PutUserPayloadV2) => OnboardingModulesApi.updateUserProfile(payload),
    onError: (error) => {
      alerts.error(t('Something went wrong'));
      sentry.log({
        category: 'auth',
        message: 'Error received in user signup with nonce token',
        data: { error },
      });
    },
    onSuccess: async () => {
      alerts.success(t('Successfully created account.'));
      authnClient.changeAuthMethod('legacy');
      try {
        await authnClient.signIn({ username: userInfo?.email, password: formValues.password });
        authnClient.changeAuthMethod('oidc');
        if (locationId) {
          setLoginData(userInfo?.userId ?? '*', { latestLocationId: locationId });
        }
        // adding v=2 to the query string to indicate that the user has completed the sign-up process through the new flow
        // we were using this query string to show loader on the dashboard page (ref: useOnboardingRedirect hook)
        navigate({ to: '/home/dashboard' });
      } catch (error) {
        sentry.log({
          category: 'auth',
          message: 'Error received in user sign-in legacy method',
          data: { error },
        });
        alerts.error(t('An error occured. Try logging in again.'));
        navigate({ to: '/sign-in' });
      }
    },
  });

  const {
    getFieldProps,
    seedValues,
    isComplete,
    values: formValues,
    formProps,
  } = useSignupUserForm({
    onSubmit: (values) => {
      if (!isComplete) {
        return;
      } else if (!passwordValidations.isSecure) {
        (formProps.ref.current?.querySelector('input[name="password"]') as HTMLInputElement)?.focus();
      } else {
        mutate({
          Nonce: nonceToken,
          Password: values.password!,
          LocationId: locationId ?? '',
          Profile: {
            FirstName: values.firstName,
            LastName: values.lastName,
            MobileNumber: values.mobileNumber,
            UserId: userInfo?.userId,
          },
          SendAppTextMessage: values.sendAppTextMessage,
        });
      }
    },
  });

  const passwordValidations = useValidatePassword({
    locationId,
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    passwordValue: formValues.password,
    userEmail: userInfo?.email,
  });

  useEffect(() => {
    if (userInfo) {
      seedValues({
        ...userInfo,
        email: maskEmail(userInfo?.email ?? ''),
      });
    }
  }, [userInfo]);

  return (
    <form css={signupFormContainerStyles} {...formProps}>
      <Heading level={2}>{t('Create Your Account')}</Heading>
      <div css={signupFormCardStyles}>
        <div css={fieldStyles}>
          <Text as='label'>{t('EMAIL')}</Text>
          <TextField
            {...getFieldProps('email')}
            label=''
            css={textFieldStyles}
            disabled={true}
            endAdornment={<Icon name='lock' color='default' />}
          />
        </div>
        <div css={fieldStyles}>
          <Text as='label'>{t('MOBILE NUMBER')}*</Text>
          <PhoneField
            {...getFieldProps('mobileNumber')}
            label=''
            css={[textFieldStyles, editableFieldStyles]}
            disabled={isLoading}
          />
        </div>
        <div css={{ display: 'flex', gap: theme.spacing(2) }}>
          <div css={fieldStyles}>
            <Text as='label'>{t('FIRST NAME')}*</Text>
            <TextField
              {...getFieldProps('firstName')}
              label=''
              css={[textFieldStyles, editableFieldStyles]}
              disabled={isLoading}
            />
          </div>
          <div css={fieldStyles}>
            <Text as='label'>{t('LAST NAME')}*</Text>
            <TextField
              {...getFieldProps('lastName')}
              label=''
              css={[textFieldStyles, editableFieldStyles]}
              disabled={isLoading}
            />
          </div>
        </div>
        <div css={fieldStyles}>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Text as='label' size='small' weight='bold'>
              {t('SET PASSWORD')}*
            </Text>
            <div {...triggerProps}>
              <Icon name='info' color='light' size={14} />
            </div>
          </div>
          <TextField
            {...getFieldProps('password')}
            onFocus={() => setPasswordFieldInFocus(true)}
            onBlur={() => setPasswordFieldInFocus(false)}
            type={showPassword ? 'text' : 'password'}
            label=''
            css={[textFieldStyles, editableFieldStyles]}
            endAdornment={
              <Icon
                css={{ cursor: 'pointer' }}
                onClick={() => setShowPassword(!showPassword)}
                name={showPassword ? 'view-off' : 'view'}
                color='light'
              />
            }
            disabled={isLoading}
          />
        </div>
      </div>
      <CheckboxField
        {...getFieldProps('sendAppTextMessage')}
        css={{ maxWidth: 400 }}
        label={
          <div css={{ display: 'flex', flexDirection: 'column' }}>
            <Text weight='bold' size='medium' as='span'>
              {t('Text me a link to download the Weave Mobile App.')}
            </Text>
            <Text size='small' as='span'>
              {t(
                'This is a one-time message from Weave. Message and data rates may apply. Reply Help for support or Stop to opt out. Your info will not be shared with third parties.'
              )}
            </Text>
          </div>
        }
        disabled={isLoading}
      />
      <NakedButton
        css={signupActionButtonStyles}
        trackingId={UserCreationTrackingIds.createAccount}
        disabled={isLoading}
        type='submit'
      >
        {t('Create Account')}
        {isLoading && <SpinningLoader size={'xs'} color='white' />}
      </NakedButton>
      <Tooltip {...tooltipProps} isOpen={tooltipProps.isOpen || passwordFieldInFocus}>
        <PasswordValidationChecks {...passwordValidations} />
      </Tooltip>
    </form>
  );
};

const ValidationCheckIcon = ({ isInvalid }: { isInvalid: boolean }) => {
  return (
    <Icon
      name={isInvalid ? 'x' : 'check'}
      aria-label={isInvalid ? 'invalid' : 'valid'}
      css={{ color: isInvalid ? theme.colors.critical30 : theme.colors.success30 }}
      size={14}
    />
  );
};

const PasswordValidationChecks = (props: ReturnType<typeof useValidatePassword>) => {
  const { t } = useTranslation('userCreate', { keyPrefix: 'signup-form' });
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
      <div
        role='alert'
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}
      >
        <Text size='small' color='white'>
          {t('{{count}} characters minimum', { count: props.minLength })}
        </Text>
        <ValidationCheckIcon isInvalid={props.isLengthShort} />
      </div>

      <div
        role='alert'
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}
      >
        <Text size='small' color='white'>
          {t('Exclude first name')}
        </Text>
        <ValidationCheckIcon isInvalid={props.hasFirstName} />
      </div>

      <div
        role='alert'
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}
      >
        <Text size='small' color='white'>
          {t('Exclude last name')}
        </Text>
        <ValidationCheckIcon isInvalid={props.hasLastName} />
      </div>

      <div
        role='alert'
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}
      >
        <Text size='small' color='white'>
          {t('Exclude user email')}
        </Text>
        <ValidationCheckIcon isInvalid={props.hasUserEmail} />
      </div>

      <div
        role='alert'
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}
      >
        <Text size='small' color='white'>
          {t('Secure Password')}
        </Text>
        {props.isCheckingSecurePW ? <SpinningLoader size='xs' /> : <ValidationCheckIcon isInvalid={!props.isSecure} />}
      </div>
    </div>
  );
};

const signupFormContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
});

const signupFormCardStyles = css({
  width: 'min(400px, 100%)',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  border: '2px solid rgba(255, 255, 255, 0.5)',
  borderRadius: theme.borderRadius.medium,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  boxShadow: '0px 3px 8px 1px rgba(0, 0, 0, 0.15)',
});

const signupActionButtonStyles = css({
  padding: theme.spacing(0, 2),
  height: theme.spacing(4),
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.neutral90,
  color: theme.colors.white,
  fontWeight: theme.font.weight.bold,
  fontSize: theme.font.size.medium,
  display: 'flex',
  alignItems: 'center',
  [':hover']: {
    backgroundColor: theme.colors.neutral80,
  },
  [':disabled']: {
    opacity: 0.5,
    backgroundColor: theme.colors.neutral30,
    color: theme.colors.neutral60,
  },
  backgroundBlendMode: 'overlay',
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  '> label': {
    fontWeight: theme.font.weight.bold,
    fontSize: theme.font.size.small,
  },
});

const textFieldStyles = css({
  background: 'rgba(255, 255, 255, 0.5)',
  border: `1px solid rgba(0, 33, 82, 0.05)`,
  boxShadow: '0px 2px 8px 0px rgba(0, 33, 82, 0.05)',
  ['> input:disabled']: {
    color: theme.colors.neutral50,
  },
});

const editableFieldStyles = css({
  backgroundColor: theme.colors.white,
});
